import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sun, Moon, ClipboardText } from '@phosphor-icons/react';
import { useAuthStore } from '../../stores/authStore';
import { useThemeStore } from '../../stores/themeStore';
import SceneGrid from './SceneGrid';
import SharedWithMe from './SharedWithMe';
import Submissions from './Submissions';
import AssetsPage from './AssetsPage';
import ReleasesPage from './ReleasesPage';
import UserMenu from '../shared/UserMenu';

type Tab = 'my-scenes' | 'shared' | 'assets' | 'submissions' | 'releases';

export default function Dashboard() {
  const { user: _user } = useAuthStore();
  const { theme, toggle } = useThemeStore();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('my-scenes');

  return (
    <div className="min-h-screen bg-bg-deepest flex flex-col">
      {/* Top nav */}
      <header className="bg-bg-surface border-b border-border-subtle px-6 h-[52px] flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <img src="/icon-512.png" alt="Spine Workspace" className="w-7 h-7 rounded-md shrink-0" />
          <span className="font-serif text-lg font-medium text-text-primary">Spine Workspace</span>
          <span className="font-sans text-xs text-text-tertiary hidden sm:block">Eighth Era Studios</span>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/review')}
            title="Review Queue"
            className="flex items-center gap-1.5 px-3 h-8 rounded-lg text-text-secondary hover:text-text-primary hover:bg-bg-hover text-xs font-sans transition-all duration-fast"
          >
            <ClipboardText size={14} />
            Review
          </button>
          <button
            onClick={toggle}
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-text-tertiary hover:text-text-primary hover:bg-bg-hover transition-all duration-fast"
          >
            {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <UserMenu size={34} />
        </div>
      </header>

      {/* Tab bar */}
      <div className="bg-bg-surface border-b border-border-subtle px-6">
        <div className="max-w-7xl mx-auto flex gap-0">
          {([
            { key: 'my-scenes',   label: 'My Scenes' },
            { key: 'shared',      label: 'Shared with me' },
            { key: 'assets',      label: 'Assets' },
            { key: 'submissions', label: 'Submissions' },
            { key: 'releases',    label: 'Releases' },
          ] as { key: Tab; label: string }[]).map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`px-4 py-3 font-sans text-sm font-medium transition-all duration-fast border-b-2 -mb-px ${
                tab === key
                  ? 'text-accent border-accent'
                  : 'text-text-secondary hover:text-text-primary border-transparent'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Main content */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-6 py-8">
        {tab === 'my-scenes'   ? <SceneGrid />    :
         tab === 'shared'      ? <SharedWithMe /> :
         tab === 'assets'      ? <AssetsPage />   :
         tab === 'releases'    ? <ReleasesPage /> :
                                 <Submissions />}
      </main>
    </div>
  );
}
